"use client";

import { useEffect, useState } from "react";
import { Box, Spinner, Heading, useTheme } from "@chakra-ui/react";
import { InfoIcon } from "@chakra-ui/icons";
import { Tooltip, Flex } from "@chakra-ui/react";
import axios from "axios";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

const Chart9 = ({ supplier, retailer }) => {
  const theme = useTheme();
  const [chartDiv, setChartDiv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(false);

  useEffect(() => {
    let root;

    const build = async () => {
      if (!chartDiv) return;
      setLoading(true);

      try {
        const { data } = await axios.post(`${process.env.REACT_APP_API_URL}/rep_sales/chart9`, { supplier, retailer, userid: localStorage.getItem("userid") });

        const values = data?.data || [];
        const allZero = values.length > 0 && values.every((v) => v.value === 0);

        if (allZero) {
          setNoData(true);
          setLoading(false);
          return;
        }

        setNoData(false);

        if (root) root.dispose();
        root = am5.Root.new(chartDiv);
        root.setThemes([am5themes_Animated.new(root)]);

        const chart = root.container.children.push(
          am5xy.XYChart.new(root, {
            panX: false,
            panY: false,
            wheelX: "none",
            wheelY: "none",
            paddingLeft: 10,
            paddingRight: 25,
            paddingBottom: 40,
          })
        );

        /* category axis – period labels */
        const xAxis = chart.xAxes.push(
          am5xy.CategoryAxis.new(root, {
            categoryField: "period",
            renderer: am5xy.AxisRendererX.new(root, {
              minGridDistance: 30,
              gridTemplate: { visible: false },
            }),
          })
        );
        const weeks = [...new Set(data.data.map((d) => d.period))];
        xAxis.data.setAll(weeks.map((w) => ({ period: w })));
        // wider spacing
        xAxis.get("renderer").setAll({
          minGridDistance: 60,
          cellStartLocation: 0, // left edge
          cellEndLocation: 1, // right edge
          gridTemplate: { visible: false },
        });

        /* centre the rotated label inside the cell */
        xAxis.get("renderer").labels.template.setAll({
          rotation: -20,
          location: 0.5,
          centerX: am5.p50,
          dy: 6,
          textAlign: "center",
          fontSize: 11,
          fill: am5.color("#9ca3af"),
        });

        /* value axis */
        const yAxis = chart.yAxes.push(
          am5xy.ValueAxis.new(root, {
            renderer: am5xy.AxisRendererY.new(root, { strokeOpacity: 0 }),
          })
        );
        yAxis.get("renderer").labels.template.setAll({ fontSize: 11, fill: am5.color("#9ca3af") });
        yAxis.get("renderer").grid.template.setAll({
          strokeOpacity: 0.15,
          strokeDasharray: [3, 3],
          stroke: am5.color("#e5e7eb"),
        });

        /* tooltip template */
        const makeTooltip = (labelText) => {
          const t = am5.Tooltip.new(root, { getFillFromSprite: false, labelText });
          t.get("background").setAll({
            fill: am5.color("#ffffff"),
            fillOpacity: 0.95,
            stroke: am5.color("#e5e7eb"),
            strokeWidth: 1,
            shadowColor: am5.color("#000000"),
            shadowBlur: 8,
            shadowOffsetX: 0,
            shadowOffsetY: 2,
            shadowOpacity: 0.08,
            cornerRadius: 6,
          });
          t.label.setAll({ fill: am5.color("#374151"), fontSize: 12 });
          return t;
        };

        /* series – Actual (columns) */
        const actualSeries = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name: "Actual",
            xAxis,
            yAxis,
            valueYField: "value",
            categoryXField: "period",
            clustered: false,
            locationX: 0.5,
            fill: am5.color("#58b6c0"),
          })
        );
        actualSeries.data.setAll(data.data.filter((d) => d.series === "Actual"));
        actualSeries.columns.template.setAll({
          width: am5.percent(60),
          strokeOpacity: 0,
          fillOpacity: 0.8,
          cornerRadiusTL: 4,
          cornerRadiusTR: 4,
          tooltip: makeTooltip("Actual: R{valueY.formatNumber('#,###')}"),
          tooltipText: "Actual: R{valueY.formatNumber('#,###')}",
        });
        /* series – Forecast (line) */
        const fcSeries = chart.series.push(
          am5xy.LineSeries.new(root, {
            name: "Forecast",
            xAxis,
            yAxis,
            valueYField: "value",
            categoryXField: "period",
            stroke: am5.color("#ef4444"),
            strokeWidth: 2,
            tooltip: makeTooltip("Forecast: R{valueY.formatNumber('#,###')}"),
          })
        );
        fcSeries.data.setAll(data.data.filter((d) => d.series === "Forecast"));
        fcSeries.bullets.push(() =>
          am5.Circle.new(root, {
            radius: 4,
            stroke: am5.color("#ef4444"),
            fill: am5.color("#fff"),
          })
        );

        /* legend */
        // chart.children.push(
        //   am5.Legend.new(root, { useDefaultMarker: true })
        // ).data.setAll([actualSeries, fcSeries]);

        chart.appear(800, 400);
        setLoading(false);
      } catch (err) {
        console.error("Chart9 fetch error", err);
        setLoading(false);
      }
    };

    build();
    return () => {
      if (root) root.dispose();
    };
  }, [chartDiv, supplier, retailer]);

  return (
    <Box w="100%" h="100%" position="relative">
      <Flex justify="space-between" align="center" mb={2}>
        <Heading size="xs" color="gray.600" fontWeight="600">Forecast vs Actual</Heading>
        <Tooltip label="Teal = weekly sales, Red line = seasonally adjusted forecast. Bars above line beat forecast; bars below miss it." hasArrow placement="left" maxW="240px" whiteSpace="normal">
          <InfoIcon w={3.5} h={3.5} cursor="pointer" color="gray.400" />
        </Tooltip>
      </Flex>

      {noData && (
        <Flex align="center" justify="center" position="absolute" top="0" left="0" width="100%" height="100%" zIndex="1">
          <Heading size="sm" color="gray.600">
            No data available
          </Heading>
        </Flex>
      )}
      <div ref={setChartDiv} style={{ width: "100%", height: "calc(100% - 32px)" }} />
      {loading && <Spinner position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" />}
    </Box>
  );
};

export default Chart9;
